/**
 * F606TrainersAdmin — trainer verification + payouts.
 * Reads trainer-scoped users (trainer_profile != null) from `/trainers`.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Loader2, ShieldCheck, ShieldOff, DollarSign } from 'lucide-react';
import { bridge, describeActions } from './bridgeClient';

export default function F606TrainersAdmin({ language }) {
  const isAr = language === 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    const res = await bridge.list('trainers', { limit: 100 });
    if (res.success !== false) setRows(res.data || []);
    else toast.error(res.error?.message || 'Failed to load trainers');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (t, fn, label, body = {}) => {
    setBusy((s) => ({ ...s, [t.id]: label }));
    const res = await fn(t.id, body);
    setBusy((s) => ({ ...s, [t.id]: null }));
    if (res.success) {
      toast.success(`✅ ${label} · ${describeActions(res.actions_fired, language)}`, { duration: 5000 });
      load();
    } else {
      toast.error(`⚠️ ${res.error?.message || 'Failed'}`);
    }
  };

  return (
    <div className="space-y-3" data-testid="f606-trainers-admin">
      <div className="flex justify-between items-center">
        <div className="text-white/60 text-xs">{rows.length} {isAr ? 'مدرّب' : 'trainers'}</div>
        <button onClick={load} className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:bg-white/10">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-black/20 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-white/5 text-white/50 text-[10px] uppercase tracking-wider">
              <th className="px-3 py-2 text-start">{isAr ? 'المدرّب' : 'Trainer'}</th>
              <th className="px-3 py-2 text-start">{isAr ? 'الإيميل' : 'Email'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'موثّق' : 'Verified'}</th>
              <th className="px-3 py-2 text-center">{isAr ? 'العملاء' : 'Clients'}</th>
              <th className="px-3 py-2 text-end">{isAr ? 'الإجراءات' : 'Actions'}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-400/50" /></td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="5" className="text-center py-8 text-white/40">{isAr ? 'لا يوجد مدرّبون' : 'No trainers'}</td></tr>
            )}
            {rows.map((t) => {
              const bId = busy[t.id];
              const profile = t.trainer_profile || {};
              const verified = t.is_verified ?? Boolean(profile.trainer_code);
              const clients = profile.students_count ?? t.clients_count ?? '—';
              return (
                <tr key={t.id} className="hover:bg-white/[0.02]" data-testid={`f606-trainer-row-${t.id}`}>
                  <td className="px-3 py-2">
                    <div className="text-white">{profile.display_name || t.full_name || t.name || '—'}</div>
                    {profile.specialty && <div className="text-[10px] text-white/40">{profile.specialty}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-white/70" dir="ltr">{t.email || '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {verified
                      ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border bg-emerald-500/15 border-emerald-500/40 text-emerald-300">✓</span>
                      : <span className="text-white/30">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-white font-mono">{clients}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {!verified ? (
                        <button
                          disabled={bId}
                          onClick={() => act(t, bridge.trainer.verify, isAr ? 'توثيق' : 'Verify')}
                          className="px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-trainer-verify-${t.id}`}
                        >
                          <ShieldCheck className="h-3 w-3 inline" /> {isAr ? 'توثيق' : 'Verify'}
                        </button>
                      ) : (
                        <button
                          disabled={bId}
                          onClick={() => {
                            const reason = window.prompt(isAr ? 'السبب:' : 'Reason:');
                            if (!reason) return;
                            act(t, bridge.trainer.unverify, isAr ? 'إلغاء توثيق' : 'Unverify', { reason });
                          }}
                          className="px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 text-[10px] font-bold disabled:opacity-40"
                          data-testid={`f606-trainer-unverify-${t.id}`}
                        >
                          <ShieldOff className="h-3 w-3 inline" /> {isAr ? 'إلغاء' : 'Unverify'}
                        </button>
                      )}
                      <button
                        disabled={bId}
                        onClick={() => {
                          const amount = Number(window.prompt(isAr ? 'المبلغ ($):' : 'Amount ($):') || '0');
                          if (!amount) return;
                          const method = window.prompt(isAr ? 'الطريقة (bank_transfer / wallet):' : 'Method (bank_transfer / wallet):') || 'bank_transfer';
                          act(t, bridge.trainer.payout, isAr ? 'دفع' : 'Payout', {
                            amount_usd: amount, method,
                          });
                        }}
                        className="px-2 py-1 rounded-md bg-purple-500/15 border border-purple-500/40 text-purple-300 hover:bg-purple-500/25 text-[10px] font-bold disabled:opacity-40"
                        data-testid={`f606-trainer-payout-${t.id}`}
                      >
                        <DollarSign className="h-3 w-3 inline" /> {isAr ? 'دفع' : 'Payout'}
                      </button>
                      {bId && <Loader2 className="h-3 w-3 animate-spin text-emerald-400/70" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
